<template>
  <div style="background-color: white; min-height: 100vh;">
    <v-layout pb-6 pr-6 pl-6 class="mainfont" wrap justify-center>
      <v-flex xs12 pt-4>
        <!-- Snackbar Notifications -->
        <v-layout wrap justify-center>
          <v-snackbar v-model="showSnackBar" :color="snackColor" top timeout="3000">
            <div class="d-flex align-center justify-space-between w-100">
              <span>{{ msg }}</span>
              <v-btn icon small dark @click="showSnackBar = false">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-snackbar>

          <!-- Loader -->
          <vue-element-loading
            :active="appLoading"
            :is-full-screen="true"
            background-color="#FFFFFF"
            color="#13736f"
            spinner="spinner"
          />
        </v-layout>

        <!-- Page Header Toolbar -->
        <div class="d-flex align-center justify-space-between flex-wrap pt-6 pb-4">
          <div class="d-flex align-center">
            <v-card
              color="#13736f"
              class="rounded-lg pa-3 mr-3 elevation-2"
              flat
              height="48"
              width="48"
              style="display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
            >
              <v-icon color="white" size="26">mdi-barcode-scan</v-icon>
            </v-card>
            <div>
              <div style="font-size: 24px; font-weight: 800; color: #1e293b; letter-spacing: -0.5px; line-height: 1.2;">
                E-TREASURY OFFICE CODES
              </div>
              <div style="font-size: 13px; color: #64748b; margin-top: 2px;">
                Treasury office mapping codes for Kerala Forest Divisions & Circles
              </div>
            </div>
          </div>

          <!-- Add Button -->
          <div class="mt-3 mt-sm-0">
            <v-btn
              color="#13736f"
              dark
              depressed
              class="text-capitalize px-4 font-weight-bold rounded-lg elevation-1"
              @click="addDialog = true"
            >
              <v-icon small left>mdi-plus-circle-outline</v-icon>
              Add E-Treasury Office Code
            </v-btn>
          </div>
        </div>

        <!-- Filter & Search Card -->
        <v-card class="elevation-1 rounded-lg pa-4 mb-4" style="border: 1px solid #e2e8f0; background: white;">
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
            <!-- Search Bar -->
            <div style="min-width: 260px; max-width: 380px;" class="flex-grow-1">
              <v-text-field
                v-model="search"
                clearable
                dense
                outlined
                hide-details
                placeholder="Search by Circle, Division, or Code..."
                background-color="white"
                color="#13736f"
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
            </div>

            <!-- Circle Filter Dropdown -->
            <div style="min-width: 220px; max-width: 300px;">
              <v-autocomplete
                v-model="selectedCircleFilter"
                :items="circle"
                item-text="officeName"
                item-value="officeName"
                clearable
                dense
                outlined
                hide-details
                placeholder="Filter by Circle"
                background-color="white"
                color="#13736f"
                prepend-inner-icon="mdi-filter-variant"
              ></v-autocomplete>
            </div>

            <!-- Total Records Count Badge -->
            <div class="caption font-weight-bold grey--text text--darken-2 d-flex align-center">
              <v-icon x-small color="#13736f" class="mr-1">mdi-counter</v-icon>
              Total: <strong class="mx-1 teal--text text--darken-3">{{ filteredDivisionCodes.length }}</strong> E-Treasury Office Codes
            </div>
          </div>
        </v-card>
      </v-flex>

      <!-- Table Card -->
      <v-flex xs12 pt-1>
        <v-card class="elevation-2 rounded-lg" style="border: 1px solid #e0e0e0; overflow: hidden;">
          <v-data-table
            :headers="headers"
            :items="filteredDivisionCodes"
            :search="search"
            class="app-custom-table"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <!-- Serial Number -->
            <template v-slot:item.sno="{ index }">
              <span class="caption font-weight-bold grey--text text--darken-2">
                {{ index + 1 }}
              </span>
            </template>

            <!-- Circle Name -->
            <template v-slot:item.circleName="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" color="teal lighten-5" class="mr-2">
                  <v-icon small color="teal darken-3">mdi-tree</v-icon>
                </v-avatar>
                <div class="font-weight-bold text-subtitle-2 blue-grey--text text--darken-4">
                  {{ item.circleName || 'N/A' }}
                </div>
              </div>
            </template>

            <!-- Division Name -->
            <template v-slot:item.divisionName="{ item }">
              <div class="d-flex align-center">
                <v-icon small color="#13736f" class="mr-2">mdi-map-marker-outline</v-icon>
                <div class="font-weight-bold text-body-2 black--text">
                  {{ item.divisionName || 'N/A' }}
                </div>
              </div>
            </template>

            <!-- DDO Code Badge -->
            <template v-slot:item.code="{ item }">
              <div
                class="pa-2 rounded-lg d-inline-block text-center elevation-1"
                style="background-color: #1a237e; color: #ffffff; min-width: 130px; border: 1px solid #0d47a1;"
              >
                <div style="font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #c5cae9;">
                  TREASURY OFFICE CODE
                </div>
                <div style="font-size: 0.95rem; font-weight: 900; letter-spacing: 0.5px;">
                  {{ item.code }}
                </div>
              </div>
              <div v-if="!isOfficeCode(item.code)" class="mt-1" style="color: #b91c1c; font-size: 12px; font-weight: 600;">
                Not an eTreasury office code (needs 11 characters). Payments will fail until this is corrected.
              </div>
            </template>

            <!-- Action Slot -->
            <template v-slot:item.action="{ item }">
              <v-btn
                small
                color="indigo darken-1"
                dark
                class="elevation-1 text-capitalize px-3 font-weight-bold rounded-lg"
                @click="openEditDialog(item)"
              >
                <v-icon small left>mdi-pencil</v-icon>
                Edit Code
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- ─── ADD DIALOG ─── -->
    <v-dialog v-model="addDialog" max-width="520px" persistent>
      <v-card class="rounded-lg overflow-hidden elevation-8">
        <v-toolbar flat dark color="#283e51" class="px-2" style="background: linear-gradient(135deg, #283e51 0%, #1a2a3a 100%);">
          <v-icon left color="#80cbc4">mdi-plus-box</v-icon>
          <v-toolbar-title class="font-weight-bold text-h6">
            Add E-Treasury Office Code
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeAddDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6 pb-2 px-6">
          <v-layout wrap>
            <!-- Circle Selection -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">
                Circle Name <span class="red--text">*</span>
              </label>
              <v-autocomplete
                v-model="addForm.circleid"
                :items="circle"
                item-text="officeName"
                item-value="_id"
                placeholder="Select Circle"
                outlined
                dense
                clearable
                hide-details
                color="#13736f"
                prepend-inner-icon="mdi-tree"
              ></v-autocomplete>
            </v-flex>

            <!-- Division Selection -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">
                Division Name <span class="red--text">*</span>
              </label>
              <v-autocomplete
                v-model="addForm.divisionid"
                :items="division"
                item-text="officeName"
                item-value="_id"
                placeholder="Select Division"
                outlined
                dense
                clearable
                hide-details
                color="#13736f"
                prepend-inner-icon="mdi-map-marker-outline"
                :disabled="!addForm.circleid"
              ></v-autocomplete>
              <div v-if="!addForm.circleid" class="caption grey--text mt-1">
                Please select a Circle first to load its divisions.
              </div>
            </v-flex>

            <!-- DDO Code Input -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">
                eTreasury office code <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="addForm.code"
                placeholder="11-character treasury office code (e.g. 01014600001)"
                hint="Enter the eTreasury office code. eTreasury rejects anything else as 'Office Code Not Registered'."
                persistent-hint
                maxlength="11"
                outlined
                dense
                color="#13736f"
                prepend-inner-icon="mdi-barcode"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-grey-lighten-4 justify-end">
          <v-btn text color="grey darken-2" class="font-weight-bold text-capitalize" @click="closeAddDialog">
            Cancel
          </v-btn>
          <v-btn
            color="#13736f"
            dark
            depressed
            class="px-5 font-weight-bold text-capitalize rounded-lg elevation-1"
            @click="submitAdd"
          >
            <v-icon small left>mdi-check</v-icon>
            Save Code
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── EDIT DIALOG ─── -->
    <v-dialog v-model="editDialog" max-width="520px" persistent>
      <v-card class="rounded-lg overflow-hidden elevation-8">
        <v-toolbar flat dark color="#283e51" class="px-2" style="background: linear-gradient(135deg, #283e51 0%, #1a2a3a 100%);">
          <v-icon left color="#80cbc4">mdi-pencil-box</v-icon>
          <v-toolbar-title class="font-weight-bold text-h6">
            Edit E-Treasury Office Code
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6 pb-2 px-6">
          <v-layout wrap>
            <!-- Circle (read-only) -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">Circle</label>
              <v-text-field
                v-model="editForm.circleName"
                outlined
                dense
                disabled
                hide-details
                prepend-inner-icon="mdi-tree"
              ></v-text-field>
            </v-flex>

            <!-- Division (read-only) -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">Division</label>
              <v-text-field
                v-model="editForm.divisionName"
                outlined
                dense
                disabled
                hide-details
                prepend-inner-icon="mdi-map-marker-outline"
              ></v-text-field>
            </v-flex>

            <!-- DDO Code (editable) -->
            <v-flex xs12 class="mb-3">
              <label class="caption font-weight-bold grey--text text--darken-2 mb-1 d-block">
                eTreasury office code <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="editForm.code"
                placeholder="11-character treasury office code"
                hint="Enter the eTreasury office code. eTreasury rejects anything else as 'Office Code Not Registered'."
                persistent-hint
                maxlength="11"
                outlined
                dense
                color="#13736f"
                prepend-inner-icon="mdi-barcode"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 justify-end">
          <v-btn text color="grey darken-2" class="font-weight-bold text-capitalize" @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="#13736f"
            dark
            depressed
            class="px-5 font-weight-bold text-capitalize rounded-lg elevation-1"
            @click="saveEdit"
          >
            <v-icon small left>mdi-content-save</v-icon>
            Update Code
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DivisionCode",
  data() {
    return {
      appLoading: false,
      showSnackBar: false,
      msg: null,
      snackColor: "black",
      search: "",
      selectedCircleFilter: null,

      // Table data
      divisionCodes: [],
      headers: [
        { text: "#", value: "sno", sortable: false, width: "60px", align: "center" },
        { text: "CIRCLE NAME", value: "circleName", sortable: true },
        { text: "DIVISION NAME", value: "divisionName", sortable: true },
        { text: "E-TREASURY OFFICE CODE", value: "code", sortable: true, align: "center" },
        { text: "ACTIONS", value: "action", sortable: false, align: "center", width: "160px" },
      ],

      // Dropdown sources
      circle: [],
      division: [],

      // Add modal
      addDialog: false,
      addForm: {
        circleid: null,
        divisionid: null,
        code: "",
      },

      // Edit modal
      editDialog: false,
      editForm: {
        _id: null,
        circleName: "",
        divisionName: "",
        code: "",
      },
    };
  },

  computed: {
    filteredDivisionCodes() {
      if (!this.selectedCircleFilter) {
        return this.divisionCodes;
      }
      return this.divisionCodes.filter(
        (item) => item.circleName === this.selectedCircleFilter
      );
    },
  },

  mounted() {
    this.getCircle();
    this.getList();
  },

  watch: {
    "addForm.circleid"(newVal) {
      this.addForm.divisionid = null;
      this.division = [];
      if (newVal) this.getDivision(newVal);
    },
  },

  methods: {
    // eTreasury office codes are 11 letters/digits (a 4-digit DDO code is rejected as "Not Registered")
    isOfficeCode(code) {
      return /^[A-Za-z0-9]{11}$/.test(String(code || "").trim());
    },
    showToast(message, color = "black") {
      this.msg = message;
      this.snackColor = color;
      this.showSnackBar = true;
    },

    // ── Fetch circles ──────────────────────────────────────────
    getCircle() {
      axios({
        method: "GET",
        url: "/get/circles",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.circle = response.data.data || [];
        })
        .catch((err) => console.log(err));
    },

    // ── Fetch divisions by circle ──────────────────────────────
    getDivision(circleid) {
      const selectedCircle = this.circle.find((c) => c._id === circleid);
      if (!selectedCircle) return;
      axios({
        method: "post",
        url: "/get/divsions",
        headers: { token: localStorage.getItem("token") },
        data: { path: selectedCircle.path },
      })
        .then((response) => {
          this.division = response.data.data || [];
        })
        .catch((err) => console.log(err));
    },

    // ── Fetch division code list ───────────────────────────────
    getList() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/divisioncode/list",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.divisionCodes = response.data.data || [];
          this.appLoading = false;
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },

    // ── Add dialog helpers ─────────────────────────────────────
    closeAddDialog() {
      this.addDialog = false;
      this.addForm = { circleid: null, divisionid: null, code: "" };
      this.division = [];
    },

    submitAdd() {
      if (!this.addForm.circleid || !this.addForm.divisionid || !this.addForm.code) {
        this.showToast("Please fill all fields.", "error");
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/divisioncode/add",
        headers: { token: localStorage.getItem("token") },
        data: {
          circleid:   this.addForm.circleid,
          divisionid: this.addForm.divisionid,
          code:       this.addForm.code,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.showToast("E-Treasury Office Code added successfully!", "success");
            this.closeAddDialog();
            this.getList();
          } else {
            this.showToast(response.data.msg || "Failed to add.", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.showToast("Error adding E-Treasury office code.", "error");
          console.log(err);
        });
    },

    // ── Edit dialog helpers ────────────────────────────────────
    openEditDialog(item) {
      this.editForm = {
        _id:          item._id,
        circleName:   item.circleName   || "",
        divisionName: item.divisionName || "",
        code:         item.code         || "",
      };
      this.editDialog = true;
    },

    saveEdit() {
      if (!this.editForm.code) {
        this.showToast("E-Treasury Office Code cannot be empty.", "error");
        return;
      }
      this.appLoading = true;
      axios({
        method: "PUT",
        url: "/divisioncode/edit",
        headers: { token: localStorage.getItem("token") },
        data: { 
          _id: this.editForm._id, 
          code: this.editForm.code,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.showToast("E-Treasury Office Code updated successfully!", "success");
            this.editDialog = false;
            this.getList();
          } else {
            this.showToast(response.data.msg || "Failed to update.", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.showToast("Error updating E-Treasury office code.", "error");
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.app-custom-table >>> th {
  background-color: #283e51 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 13.5px !important;
  padding: 14px 16px !important;
  white-space: nowrap;
}

.app-custom-table >>> td {
  padding: 14px 16px !important;
  vertical-align: middle;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.95rem !important;
}

.app-custom-table >>> tr:hover {
  background-color: #f8fafc !important;
}
</style>
